// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Lightbox.tsx
 * @input Uses React, native dialog, StyleX, IconButton, theme tokens
 * @output Exports Lightbox component, LightboxProps, LightboxMedia
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Lightbox/Lightbox.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Lightbox/Lightbox.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Lightbox/index.ts (exports if types change)
 * - /apps/storybook/stories/Lightbox.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Lightbox/ (showcase blocks)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { Icon } from "../Icon/index.js";
import { IconButton } from "../IconButton/index.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import { useScrollLock } from "../hooks/useScrollLock.js";
import { useIsomorphicLayoutEffect } from "../hooks/useIsomorphicLayoutEffect.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/**
 * Media type for lightbox items.
 */

/**
 * Describes a single media item in a lightbox.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  dialog: {
    kVAEAm: "xixxii4",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xn9wirt",
    kZKoxP: "x1dr59a3",
    ks0D6T: "x1x1rfll",
    kskxy: "x7ab17h",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kWkggS: "xjbqb8w",
    kVQacm: "xb3r6kr",
    kI3sdo: "x1a2a7pz",
    kGyWv1: "xnixb3f",
    kba3nw: "x1abwkk1",
    $$css: true
  },
  image: {
    ks0D6T: "x193iq5w",
    kskxy: "xmz0i5r",
    kVIFPx: "x19kjcj4",
    kfzvcC: "x47corl",
    k1ekBW: "x11xpdln",
    kIyJzY: "x13dflua x12w9bfk",
    kAMwcw: "x9lcvmn",
    $$css: true
  },
  imageDragging: {
    k1ekBW: "x13b0p5u",
    $$css: true
  },
  controlButton: {
    kMwMTN: "x9e3rv5",
    $$css: true
  }
};
const dynamicStyles = {
  imageTransform: transform => [{
    k3aq6I: transform != null ? "xsqj5wx" : transform,
    $$css: true
  }, {
    "--x-transform": transform != null ? transform : undefined
  }]
};

/**
 * A fullscreen overlay for viewing images at full resolution.
 *
 * Supports single image and gallery modes. In gallery mode, provides
 * prev/next navigation via buttons and arrow keys. Optionally supports
 * zoom (double-click to toggle 2x) and pan (drag when zoomed).
 *
 * Uses the native `<dialog>` element with `showModal()` for focus
 * trapping and top-layer placement. Dismiss via Escape, close button,
 * or backdrop click.
 *
 * @example
 * ```
 * <Lightbox
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   media={{src: "/photo.jpg", alt: "A photo"}}
 * />
 * <Lightbox
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   media={photos}
 * />
 * <Lightbox
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   media={photos}
 *   index={currentIndex}
 *   onIndexChange={setCurrentIndex}
 * />
 * ```
 */
export function Lightbox({
  isOpen,
  onOpenChange,
  media,
  index: controlledIndex,
  defaultIndex = 0,
  onIndexChange,
  hasZoom = false,
  hasAutoPlay = false,
  xstyle,
  className,
  style,
  ref,
  onClick: onClickProp,
  onKeyDown: onKeyDownProp,
  ...props
}) {
  const t = useTranslator();
  const dialogRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const triggerElementRef = useRef(null);

  // Index state (controlled + uncontrolled)
  const isControlled = controlledIndex !== undefined;
  const [uncontrolledIndex, setUncontrolledIndex] = useState(defaultIndex);
  const index = isControlled ? controlledIndex : uncontrolledIndex;
  const setIndex = useCallback(value => {
    if (!isControlled) {
      setUncontrolledIndex(value);
    }
    onIndexChange?.(value);
  }, [isControlled, onIndexChange, setUncontrolledIndex]);

  // Zoom/pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({
    x: 0,
    y: 0,
    panX: 0,
    panY: 0
  });

  // Resolve current media item
  const mediaArray = useMemo(() => Array.isArray(media) ? media : [media], [media]);
  const isGallery = mediaArray.length > 1;
  const currentItem = mediaArray.length > 0 ? mediaArray[Math.min(index, mediaArray.length - 1)] : null;
  const currentType = currentItem?.type ?? 'image';
  const isVideo = currentType === 'video';
  const canPrev = isGallery && index > 0;
  const canNext = isGallery && index < mediaArray.length - 1;

  // Scroll lock
  useScrollLock(isOpen);

  // Reset zoom on image change
  useEffect(() => {
    // Reset image view state when the active media item changes.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setZoom(1);
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setPan({
      x: 0,
      y: 0
    });
  }, [index, currentItem?.src]);

  // Announce gallery navigation to screen readers. Moving between images only
  // updates the visual counter, which is silent to assistive tech, so mirror
  // each change in a polite live region ("<alt>, 3 of 12", or "Image 3 of 12"
  // when the image has no alt). Announce only when the image changes during an
  // already-open session — not on mount, not when opening (even at a new
  // index, since the dialog's aria-label already names the current image), and
  // not on close.
  const announce = useAnnounce();
  const prevIndexRef = useRef(index);
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const indexChanged = prevIndexRef.current !== index;
    const wasOpen = wasOpenRef.current;
    prevIndexRef.current = index;
    wasOpenRef.current = isOpen;
    if (!indexChanged || !isOpen || !wasOpen) {
      return;
    }
    const item = mediaArray[Math.min(index, mediaArray.length - 1)];
    const position = `${index + 1} of ${mediaArray.length}`;
    announce(item?.alt ? `${item.alt}, ${position}` : `Image ${position}`);
  }, [index, isOpen, announce, mediaArray]);

  // Open/close dialog
  useIsomorphicLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    if (isOpen && !dialog.open) {
      triggerElementRef.current = document.activeElement;
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
      if (triggerElementRef.current instanceof HTMLElement) {
        triggerElementRef.current.focus();
      }
    }
  }, [isOpen]);
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Escape key
  const handleCancel = useCallback(e => {
    e.preventDefault();
    handleClose();
  }, [handleClose]);

  // Backdrop click
  const handleBackdropClick = useCallback(e => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // Gallery navigation
  const goToPrev = useCallback(() => {
    if (canPrev) {
      setIndex(index - 1);
    }
  }, [canPrev, index, setIndex]);
  const goToNext = useCallback(() => {
    if (canNext) {
      setIndex(index + 1);
    }
  }, [canNext, index, setIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback(e => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goToPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goToNext();
    }
  }, [goToPrev, goToNext]);

  // Zoom: double-click toggles 1x ↔ 2x
  const handleDoubleClick = useCallback(() => {
    if (!hasZoom) {
      return;
    }
    if (zoom === 1) {
      setZoom(2);
      setPan({
        x: 0,
        y: 0
      });
    } else {
      setZoom(1);
      setPan({
        x: 0,
        y: 0
      });
    }
  }, [hasZoom, zoom]);

  // Pan: mouse drag when zoomed
  const handlePointerDown = useCallback(e => {
    if (zoom <= 1 || !hasZoom) {
      return;
    }
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y
    };
  }, [zoom, hasZoom, pan]);
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const handlePointerMove = e => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setPan({
        x: dragStartRef.current.panX + dx,
        y: dragStartRef.current.panY + dy
      });
    };
    const handlePointerUp = () => {
      setIsDragging(false);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);
  const isZoomed = zoom > 1;
  const imageTransform = zoom === 1 ? null : `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`;
  if (!currentItem) {
    return null;
  }
  return /*#__PURE__*/_jsx("dialog", {
    ref: mergeRefs(ref, dialogRef),
    onCancel: handleCancel,
    onClick: e => {
      handleBackdropClick(e);
      onClickProp?.(e);
    },
    onKeyDown: e => {
      handleKeyDown(e);
      onKeyDownProp?.(e);
    },
    "aria-label": currentItem.alt || t('@astryx.lightbox.mediaViewer'),
    ...mergeProps(themeProps('lightbox'), stylex.props(styles.dialog, xstyle), className, style),
    ...props,
    children: /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k xh8yej3 x5yr21d x1n2onr6"
      },
      children: [/*#__PURE__*/_jsx("div", {
        ...{
          className: "x10l6tqk xyx6v2t xmz3bnw x1vjfegm"
        },
        children: /*#__PURE__*/_jsx(IconButton, {
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            size: "sm",
            color: "inherit"
          }),
          label: t('@astryx.lightbox.close'),
          variant: "ghost",
          onClick: handleClose,
          xstyle: styles.controlButton
        })
      }), isGallery && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x10l6tqk xwa60dl x1cb1t30 x1vjfegm xnp31yv"
        },
        children: /*#__PURE__*/_jsx(IconButton, {
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "chevronLeft",
            size: "sm",
            color: "inherit"
          }),
          label: t('@astryx.lightbox.previous'),
          variant: "ghost",
          isDisabled: !canPrev,
          onClick: goToPrev,
          xstyle: styles.controlButton
        })
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf x6s0dn4 x193iq5w xmz0i5r xb3r6kr"
        },
        children: [/*#__PURE__*/_jsx("div", {
          ref: imageWrapperRef,
          ...{
            0: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr xt0e3qv x87ps6o x2lwn1j"
            },
            4: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j x1huxd7x x2dt3px"
            },
            2: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j x1jm3nie"
            },
            6: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j x1jm3nie"
            },
            1: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j xi9pz9s"
            },
            5: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j xi9pz9s"
            },
            3: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j xi9pz9s"
            },
            7: {
              className: "x78zum5 x6s0dn4 xl56j7k xb3r6kr x87ps6o x2lwn1j xi9pz9s"
            }
          }[!!(!isVideo && hasZoom && !isZoomed) << 2 | !!(!isVideo && isZoomed) << 1 | !!(!isVideo && isDragging) << 0],
          onDoubleClick: isVideo ? undefined : handleDoubleClick,
          onPointerDown: isVideo ? undefined : handlePointerDown,
          children: isVideo ? /*#__PURE__*/_jsx("video", {
            src: currentItem.src,
            "aria-label": currentItem.alt,
            controls: true,
            autoPlay: hasAutoPlay,
            ...{
              className: "x193iq5w xmz0i5r x19kjcj4 x1a2a7pz"
            }
          }) : /*#__PURE__*/_jsx("img", {
            src: currentItem.src,
            alt: currentItem.alt,
            draggable: false,
            ...stylex.props(styles.image, isDragging && styles.imageDragging, imageTransform != null && dynamicStyles.imageTransform(imageTransform))
          })
        }), currentItem.caption && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x9e3rv5 x18juvz8 xf74fhv x2b8uid x1xye8es x18d9i69 xrrkdod xrlsmeg x2lah0s"
          },
          children: currentItem.caption
        })]
      }), isGallery && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x10l6tqk xwa60dl x1cb1t30 x1vjfegm xmz3bnw"
        },
        children: /*#__PURE__*/_jsx(IconButton, {
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "chevronRight",
            size: "sm",
            color: "inherit"
          }),
          label: t('@astryx.lightbox.next'),
          variant: "ghost",
          isDisabled: !canNext,
          onClick: goToNext,
          xstyle: styles.controlButton
        })
      }), isGallery && mediaArray.length > 1 && /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x10l6tqk xyx6v2t xnp31yv x9e3rv5 xjm74w1 xw6l6zx x1vjfegm"
        },
        children: [index + 1, " / ", mediaArray.length]
      })]
    })
  });
}
Lightbox.displayName = 'Lightbox';